import { Link } from "@/src/lib/next-compat";
import LandingPageShell from "@/components/landing-page/layout/LandingPageShell";

export const metadata = {
  title: "Privacy Policy · inaraX",
};

export default function PrivacyPolicyPage() {
  return (
    <LandingPageShell surface="white" className="text-slate-900">
      <div className="container mx-auto px-4 py-10 md:py-16 lg:py-20 max-w-4xl">
        <header className="mb-10 md:mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span>Legal · Privacy Policy</span>
          </div>
          <h1 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
            How inaraX handles your data
          </h1>
          <p className="mt-3 max-w-2xl text-sm md:text-base text-slate-600">
            This Privacy Policy explains what personal data we collect when you use inaraX, how we use and
            share it, and the choices you have. It is written to describe what our code actually does today,
            not a hypothetical future product.
          </p>
        </header>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm px-5 py-8 md:px-8 md:py-10">
          <section className="prose prose-slate max-w-none">
            <h2 className="mt-0 text-xl md:text-2xl font-semibold text-slate-900">
              1. Who we are
            </h2>
            <p>
              inaraX is a learning platform that uses AI to personalize technical education. When we say
              “we”, “us” or “our” in this Privacy Policy, we are referring to the team operating the inaraX
              platform and related services.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              2. What data we collect
            </h2>
            <p>
              Based on how our application is built, we collect and process the following categories of
              personal data when you use inaraX:
            </p>
            <h3 className="mt-6 text-lg font-semibold text-slate-900">
              a) Account and identity data
            </h3>
            <p>
              When you sign up or sign in, we receive identity information from our authentication
              provider, including:
            </p>
            <ul>
              <li>Name and email address</li>
              <li>Profile image (if you choose to use one)</li>
              <li>Identifiers associated with your account (for example, user IDs used internally in our database)</li>
              <li>Organization membership information where your account is linked to an organization</li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-slate-900">
              b) Learning and assessment data
            </h3>
            <p>
              Our core product is built around placement quizzes, lessons and continuous assessment. To
              power this, we store:
            </p>
            <ul>
              <li>Your course enrollments and progression (which courses you are in, which modules and lessons you have opened or completed)</li>
              <li>Placement quiz attempts, scores, and derived placement levels</li>
              <li>
                Quiz submissions and outcomes, including your answers, grading results, and signals our
                system derives (for example, where you are struggling or advancing quickly)
              </li>
              <li>
                Generated lesson variants and personalized or remedial lessons that are created for you based
                on your domain, goals and performance
              </li>
              <li>
                Credential and certificate information where applicable (for example, badges and their
                status, issue date and renewal history)
              </li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-slate-900">
              c) Preference and settings data
            </h3>
            <p>
              To personalize your learning path, our code stores per-course user settings such as:
            </p>
            <ul>
              <li>Which course you are learning in (course ID)</li>
              <li>Your chosen domain or track within a course</li>
              <li>Your future goal or outcome for that course (for example, a target role)</li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-slate-900">
              d) Usage, log and technical data
            </h3>
            <p>
              Like most web applications, we collect technical information about how you access and use
              inaraX, including:
            </p>
            <ul>
              <li>IP address, browser type, and basic device information</li>
              <li>Request metadata such as timestamps, URLs, and internal request IDs</li>
              <li>Error logs and performance metrics that help us debug and improve reliability</li>
            </ul>

            <h3 className="mt-6 text-lg font-semibold text-slate-900">
              e) Cookies and similar technologies
            </h3>
            <p>
              We use cookies and similar technologies to keep you signed in, remember your preferences and
              understand how the platform is used. For more detail on the specific cookies we use and how to
              manage them, please see our{" "}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Cookie Policy
              </Link>
              .
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              3. How we use your data
            </h2>
            <p>We use the data described above for the following purposes:</p>
            <ul>
              <li>
                <strong>Providing and maintaining the service</strong>: operating the inaraX platform,
                authentication, account management, course delivery and core features like quizzes and
                certificates.
              </li>
              <li>
                <strong>Personalizing your learning path</strong>: using your quiz signals, placement level,
                preferences and progress to determine your next lesson, generate proactive and remedial
                lessons, and adjust content difficulty.
              </li>
              <li>
                <strong>Improving our product</strong>: analyzing anonymized or aggregated usage patterns,
                debugging issues, and making product decisions based on how people actually use the platform.
              </li>
              <li>
                <strong>Security and abuse prevention</strong>: detecting suspicious activity, protecting user
                accounts, and keeping the platform safe.
              </li>
              <li>
                <strong>Legal and compliance</strong>: keeping records that are reasonably necessary to meet
                our legal, regulatory, and contractual obligations.
              </li>
            </ul>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              4. Legal bases for processing
            </h2>
            <p>
              We process your personal data only where we have a valid legal basis, which depends on the
              context:
            </p>
            <ul>
              <li>
                <strong>Contract</strong>: we need to process your data to provide the service you sign up for
                (for example, to create your account, enroll you in courses, and track your progress).
              </li>
              <li>
                <strong>Legitimate interests</strong>: we process data to secure and improve our platform and
                to understand how it is used, in ways that do not override your privacy rights.
              </li>
              <li>
                <strong>Consent</strong>: where required by law (for example, for certain cookies or
                marketing), we rely on your consent, which you can withdraw at any time.
              </li>
            </ul>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              5. How we share your data
            </h2>
            <p>
              We do not sell your personal data. We share it only with:
            </p>
            <ul>
              <li>
                <strong>Service providers</strong> that help us run the platform, such as cloud hosting,
                databases, authentication, and analytics. These providers are only allowed to process your
                data on our instructions and under appropriate data protection safeguards.
              </li>
              <li>
                <strong>Organization admins</strong> where your account is part of an organization account
                (for example, your employer or education partner), to the extent needed to provide reporting,
                credentialing, or other agreed features.
              </li>
              <li>
                <strong>Legal and safety partners</strong> where we are required to do so by law, regulation
                or a valid legal process, or where we believe it is necessary to protect the rights, safety
                or property of our users, our platform, or others.
              </li>
            </ul>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              6. International transfers
            </h2>
            <p>
              Our infrastructure and service providers may be located in different countries. This means your
              personal data may be processed outside of your country of residence. Where this happens, we
              take reasonable steps to ensure that your data is protected with appropriate safeguards and
              that transfers are made in compliance with applicable data protection laws.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              7. Data retention
            </h2>
            <p>
              We keep your personal data for as long as your account is active or as long as we reasonably
              need it to provide the service, comply with our legal obligations, resolve disputes, and
              enforce our agreements.
            </p>
            <p>
              When you close your account or when we no longer need certain data, we will either delete it or
              anonymize it. Some data may be retained for a longer period in backups or logs where immediate
              deletion is not technically feasible, but it will be stored securely and isolated from normal
              operational use.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              8. Your rights and choices
            </h2>
            <p>
              Depending on your location, you may have rights over your personal data, including the right to:
            </p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request corrections to inaccurate or incomplete data</li>
              <li>Request deletion of your data in certain circumstances</li>
              <li>Object to or request restriction of certain processing</li>
              <li>Withdraw consent where we rely on consent</li>
            </ul>
            <p>
              We will respond to requests to exercise these rights in line with applicable laws. In some
              cases, we may need to keep certain data (for example, for legal or security reasons), and we
              will explain this if it applies.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              9. Security
            </h2>
            <p>
              We use technical and organizational measures to protect your personal data, including access
              controls, encryption in transit, and monitoring for unusual activity. However, no online service
              can ever be guaranteed to be 100% secure, so we cannot promise absolute security.
            </p>
            <p>
              You are responsible for keeping your login credentials safe and for notifying us promptly if you
              suspect unauthorized access to your account.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              10. Children&apos;s privacy
            </h2>
            <p>
              inaraX is not directed at children under the minimum age required to use online services in
              their country (for example, 13 or older in many jurisdictions). We do not knowingly collect
              personal data from children under this age. If we learn that we have collected such data, we
              will take reasonable steps to delete it.
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              11. Changes to this policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our product, our
              practices, or applicable laws. When we make material changes, we will update the date at the top
              of this page and, where appropriate, provide additional notice (for example, in the app).
            </p>

            <h2 className="mt-10 text-xl md:text-2xl font-semibold text-slate-900">
              12. Contact us
            </h2>
            <p>
              If you have questions about this Privacy Policy or how we handle your data, please contact us
              using the support channels provided in the inaraX platform.
            </p>
          </section>
        </div>
      </div>
    </LandingPageShell>
  );
}

